'use client';

import { HStack, Text, VStack } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { ForwardRefRenderFunction, forwardRef } from 'react';

interface Props {
  transaction: AdaptedGiftcardTransactionIntent;
  content: GiftcardTransactionsListContent;
}

const GiftcardTransactionIntentItemBase: ForwardRefRenderFunction<
  HTMLDivElement,
  Props
> = (props, ref) => {
  const { transaction, content } = props;

  const { statementDescriptor, type, formatted, amount } = transaction;

  const transactionTypeLabel: Record<GiftcardTransactionType, string> = {
    DEBIT: content.debit,
    CREDIT: content.credit,
    CANCELLATION: content.cancellation,
    DEPRECIATION: content.depreciation,
    REFUND: content.refund,
  };

  return (
    <HStack
      justify="space-between"
      data-testid="giftcard-transaction-item"
      key={transaction.transactionIntentId}
      w="full"
      as={motion.div}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1, transition: { delay: 0.25 } }}
      transition={{ type: 'spring' }}
      ref={ref}
    >
      <VStack
        align="flex-start"
        spacing="0.25rem"
        data-testid="giftcard-transaction-item-infos"
      >
        <Text fontSize="0.9375rem" color="network.accent">
          {content.reservedUntil} {formatted.expiresAt}{' '}
          {formatted.expiresAtTime}
        </Text>

        <Text fontSize="1.125rem">{statementDescriptor}</Text>

        <Text fontSize="0.9375rem" color="network.accent">
          {transactionTypeLabel[type]}
        </Text>
      </VStack>

      <Text fontSize="1.25rem" color={amount < 0 ? 'red.500' : 'blue.800'}>
        {`${amount > 0 ? '+' : ''}${formatted.amount}`}
      </Text>
    </HStack>
  );
};

export const GiftcardTransactionIntentItem = forwardRef(
  GiftcardTransactionIntentItemBase,
);
